import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialog,
} from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { NgToastService } from 'ng-angular-popup';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Components
import { RichTextEditorComponent } from '@/app/components/shared/rich-textEditor/rich-textEditor.component';

// Services
import { ProjectStageApprovalService } from '@/app/services/projects/project-stage-approval.service';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { UserService } from '@/app/services/user.service';
import { DomSanitizer } from '@angular/platform-browser';

interface FeedbackState {
  feedback: string;
  decision: ApprovalDecision | '';
}

interface DialogData {
  projectId: number;
  stage: number;
}

export interface ApprovalFeedbackResponse {
  success: boolean;
  message?: string;
}

export interface ApprovalFeedbackRequest {
  project_id: number;
  stage: number;
  feedback: string;
  status: string;
}

const INITIAL_FEEDBACK_STATE: FeedbackState = {
  feedback: '',
  decision: '',
};

enum ApprovalDecision {
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PENDING = 'pending',
  ON_HOLD = 'on hold',
  NEED_CLARIFICATION = 'need Clarification',
  PENDING_REVIEW = 'pending Review',
  REASSIGN = 'reassign',
  SEND_FOR_REVISION = 'send For Revision',
  APPROVE_WITH_CONDITIONS = 'approve With Conditions'
}

@Component({
  selector: 'app-approval-feedback',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RichTextEditorComponent,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatIconModule,
    MatFormFieldModule,MatSelectModule
  ],
  templateUrl: './approval-feedback.component.html',
  styleUrl: './approval-feedback.component.css',
})
export class ApprovalFeedbackComponent implements OnDestroy,OnInit {
  private readonly state$ = new BehaviorSubject<FeedbackState>(
    INITIAL_FEEDBACK_STATE
  );
  readonly currentState$ = this.state$.asObservable();
  readonly approvalDecisions = Object.values(ApprovalDecision);

  resetRichTextEditor = false;
  isLoading = false;
  errorMessage = '';
  usersList: any;
  Comment:boolean=false;
  senderDetail: { description?: string; userName?: string; userTitle?: string } = {};

  roles = [
    'Admin',
    'Project Manager',
    'Site Supervisor',
    'Civil Engineer',
    'Quantity Surveyor',
    'Architect',
    'Safety Officer',
    'Construction Manager',
    'Procurement Officer',
    'Foreman',
  ];
  constructor(
    private readonly toast: NgToastService,
    private projectStageApprovalService: ProjectStageApprovalService,
    public dialogRef: MatDialogRef<ApprovalFeedbackComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly dialogData: DialogData,
    public dialog: MatDialog,
    private userService: UserService,
    private sanitizer: DomSanitizer
  ) {}
  ngOnInit(): void {
    this.fetchUsers();
    this.stageApprovals();
  }

  stageApprovals() {
    this.projectStageApprovalService.getProjectStageApprovals(this.dialogData.projectId, this.dialogData.stage).subscribe({
      next: (response:any) => {
        if (response?.data) {
          this.senderDetail = {
            description: response?.data[0]?.notes,
            userName: response?.data[0]?.created_by?.name,
            userTitle: response?.data[0]?.subject,
          };
          console.log(this.senderDetail);
          console.log(response?.data);
          
        }
      },
      error: (err) => {
        console.error("Error fetching approvals", err);
      }
    });
  }

  requests = [
    {
      name: 'Emily Carter',
      role: 'Project Manager',
      subject: 'Project Brief',
      description: 'I have completed the required details and am submitting them for your review and approval. Please review the information and provide your feedback at your earliest convenience.'
    }
  ];
  get decision(): ApprovalDecision | '' {
    return this.state$.value.decision;
  }

  set decision(value: ApprovalDecision | '') {
    this.updateState({ decision: value });
    this.errorMessage = '';
  }

  ngOnDestroy(): void {
    this.state$.complete();
  }
  closeDialog(): void {
    this.dialog.closeAll();
  }

  onClose(): void {
    this.dialogRef.close();
  }

  addNote(content: string): void {
    this.updateState({ feedback: content });
    this.errorMessage = '';
    this.Comment = true; 
  }

  private validateForm(): boolean {
    if (!this.dialogData.projectId) {
      this.errorMessage = 'Project ID not found.';
      this.toast.danger(this.errorMessage);
      return false;
    }

    const currentState = this.state$.value;

    if (!currentState.decision) {
      this.errorMessage = 'Please select a decision option.';
      this.toast.danger(this.errorMessage);
      return false;
    }

    if (!currentState.feedback.trim()) {
      this.errorMessage = 'Please add feedback.';
      this.toast.danger(this.errorMessage);
      return false;
    }

    return true;
  }

  async sendApprovalFeedbackRequest(): Promise<void> {
    if (!this.validateForm() || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const currentState = this.state$.value;
    const transformedData: ApprovalFeedbackRequest = {
      project_id: this.dialogData.projectId,
      stage: this.dialogData.stage,
      feedback: currentState.feedback,
      status: currentState.decision,
    };

    try {
      const response = await this.projectStageApprovalService
        .updateApprovalFeedback(transformedData)
        .toPromise();

      this.toast.success('Feedback submitted successfully!');
      this.handleSuccessfulSubmission(response);
    } catch (error: any) {
      this.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private handleSuccessfulSubmission(approvalResponse: any): void {
    const updatedApproval = approvalResponse?.data || [];

    /* Create an array*/
    const approvalArray = Array.isArray(updatedApproval)
      ? updatedApproval
      : [updatedApproval];

    /** Reset state to prepare for next interaction*/
    this.resetState();

    /** Send updated updatedApproval back to the parent*/
    this.dialogRef.close({ success: true, updatedApproval: approvalArray });
  }

  private handleError(error: any): void {
    console.error('Error sending approval feedback:', error);
    this.errorMessage =
      error.error?.message || 'Failed to send approval. Please try again.';
    this.toast.danger(this.errorMessage);
  }

  private resetState(): void {
    this.updateState({ ...INITIAL_FEEDBACK_STATE });
    this.resetRichTextEditor = true;
    this.errorMessage = '';
  }

  private updateState(partialState: Partial<FeedbackState>): void {
    this.state$.next({
      ...this.state$.value,
      ...partialState,
    });
  }
  

  fetchUsers() {
    this.userService.getUsers().subscribe(
      (response: any) => {
        if (response) {
          this.usersList = response.map((user: any) => ({
            id: user?.id,
            name: user?.name,
            email: user?.email,
            role: this.roles[Math.floor(Math.random() * this.roles.length)],
          }));
        } else {
          console.error("Response does not contain 'data' property.");
        }
      },
      (error) => {
        console.log('Failed to load users: ', error);
      }
    );
  }

  getSanitizedHtml(text: string) {
    if (!text) {
      return '';
    }
    return this.sanitizer.bypassSecurityTrustHtml(text);
  }
}
